<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LiaAgentsAPI from 'dashboard/api/liaAgents';

const props = defineProps({
  agent: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const items = ref([]);
const isLoading = ref(false);
const showAddTextModal = ref(false);
const editingItem = ref(null);

// Form state
const formTitle = ref('');
const formContent = ref('');
const isSaving = ref(false);

// File upload
const fileInput = ref(null);
const isUploading = ref(false);

// Polling for processing items
let pollInterval = null;

const agentId = computed(() => props.agent?.id);
const isSyncing = ref(false);

const syncKnowledge = async () => {
  if (!agentId.value) return;
  isSyncing.value = true;
  try {
    const response = await LiaAgentsAPI.syncKnowledge(agentId.value);
    if (response.data.success) {
      useAlert(t('LIA_AGENTS.KNOWLEDGE.SYNC_SUCCESS'));
    } else {
      useAlert(response.data.error || t('LIA_AGENTS.KNOWLEDGE.SYNC_ERROR'));
    }
  } catch (error) {
    const msg = error.response?.data?.error || error.message;
    useAlert(msg || t('LIA_AGENTS.KNOWLEDGE.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const hasProcessingItems = computed(() =>
  items.value.some(i => i.status === 'pending' || i.status === 'processing')
);

const statusConfig = computed(() => ({
  pending: {
    label: t('LIA_AGENTS.KNOWLEDGE.STATUS_PENDING'),
    class:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: 'i-lucide-clock',
  },
  processing: {
    label: t('LIA_AGENTS.KNOWLEDGE.STATUS_PROCESSING'),
    class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: 'i-lucide-loader-2 animate-spin',
  },
  ready: {
    label: t('LIA_AGENTS.KNOWLEDGE.STATUS_READY'),
    class:
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: 'i-lucide-check',
  },
  error: {
    label: t('LIA_AGENTS.KNOWLEDGE.STATUS_ERROR'),
    class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: 'i-lucide-alert-circle',
  },
}));

const sourceTypeIcon = type => {
  const icons = {
    text: 'i-lucide-file-text',
    pdf: 'i-lucide-file-type',
    txt: 'i-lucide-file',
    docx: 'i-lucide-file-type-2',
  };
  return icons[type] || 'i-lucide-file';
};

const sourceTypeLabel = type => {
  const labels = {
    text: t('LIA_AGENTS.KNOWLEDGE.TYPE_TEXT'),
    pdf: t('LIA_AGENTS.KNOWLEDGE.TYPE_PDF'),
    txt: t('LIA_AGENTS.KNOWLEDGE.TYPE_TXT'),
    docx: t('LIA_AGENTS.KNOWLEDGE.TYPE_DOCX'),
  };
  return labels[type] || type;
};

const fetchItems = async () => {
  if (!agentId.value) return;
  isLoading.value = true;
  try {
    const response = await LiaAgentsAPI.getKnowledgeItems(agentId.value);
    items.value = response.data;
  } catch {
    useAlert(t('LIA_AGENTS.KNOWLEDGE.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const startPollingIfNeeded = () => {
  if (pollInterval) return;
  if (!hasProcessingItems.value) return;

  pollInterval = setInterval(async () => {
    await fetchItems();
    if (!hasProcessingItems.value) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  }, 5000);
};

const openAddText = () => {
  editingItem.value = null;
  formTitle.value = '';
  formContent.value = '';
  showAddTextModal.value = true;
};

const openEditText = item => {
  editingItem.value = item;
  formTitle.value = item.title;
  formContent.value = item.content || '';
  showAddTextModal.value = true;
};

const saveTextItem = async () => {
  if (!formTitle.value.trim()) {
    useAlert(t('LIA_AGENTS.KNOWLEDGE.TITLE_REQUIRED'));
    return;
  }
  isSaving.value = true;
  try {
    if (editingItem.value) {
      await LiaAgentsAPI.updateKnowledgeItem(
        agentId.value,
        editingItem.value.id,
        {
          title: formTitle.value,
          content: formContent.value,
        }
      );
      useAlert(t('LIA_AGENTS.KNOWLEDGE.ITEM_UPDATED'));
    } else {
      await LiaAgentsAPI.createKnowledgeItem(agentId.value, {
        title: formTitle.value,
        content: formContent.value,
        source_type: 'text',
      });
      useAlert(t('LIA_AGENTS.KNOWLEDGE.ITEM_ADDED'));
    }
    showAddTextModal.value = false;
    await fetchItems();
    startPollingIfNeeded();
  } catch (error) {
    useAlert(
      t('LIA_AGENTS.KNOWLEDGE.SAVE_ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isSaving.value = false;
  }
};

const triggerFileUpload = () => {
  fileInput.value?.click();
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    useAlert(t('LIA_AGENTS.KNOWLEDGE.FILE_TOO_LARGE'));
    return;
  }

  const allowedTypes = [
    'application/pdf',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  if (!allowedTypes.includes(file.type)) {
    useAlert(t('LIA_AGENTS.KNOWLEDGE.FILE_UNSUPPORTED'));
    return;
  }

  isUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('file', file);

    await LiaAgentsAPI.createKnowledgeItemWithFile(agentId.value, formData);
    useAlert(t('LIA_AGENTS.KNOWLEDGE.FILE_UPLOADED'));
    await fetchItems();
    startPollingIfNeeded();
  } catch (error) {
    useAlert(
      t('LIA_AGENTS.KNOWLEDGE.UPLOAD_ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isUploading.value = false;
    // Reset file input
    if (fileInput.value) fileInput.value.value = '';
  }
};

const deleteItem = async item => {
  if (
    !window.confirm(
      t('LIA_AGENTS.KNOWLEDGE.DELETE_CONFIRM', { name: item.title })
    )
  ) {
    return;
  }
  try {
    await LiaAgentsAPI.deleteKnowledgeItem(agentId.value, item.id);
    useAlert(t('LIA_AGENTS.KNOWLEDGE.ITEM_DELETED'));
    await fetchItems();
  } catch {
    useAlert(t('LIA_AGENTS.KNOWLEDGE.DELETE_ERROR'));
  }
};

const reprocessItem = async item => {
  try {
    await LiaAgentsAPI.reprocessKnowledgeItem(agentId.value, item.id);
    useAlert(t('LIA_AGENTS.KNOWLEDGE.REPROCESSING'));
    await fetchItems();
    startPollingIfNeeded();
  } catch {
    useAlert(t('LIA_AGENTS.KNOWLEDGE.REPROCESS_ERROR'));
  }
};

onMounted(() => {
  fetchItems();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div
      class="bg-n-solid-3 rounded-xl overflow-hidden border border-n-weak p-5"
    >
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ $t('LIA_AGENTS.KNOWLEDGE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{ $t('LIA_AGENTS.KNOWLEDGE.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 rounded-lg transition-colors"
            @click="openAddText"
          >
            <span class="i-lucide-plus size-4" />
            {{ $t('LIA_AGENTS.KNOWLEDGE.ADD_TEXT') }}
          </button>
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isUploading"
            @click="triggerFileUpload"
          >
            <span
              v-if="isUploading"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-upload size-4" />
            {{ $t('LIA_AGENTS.KNOWLEDGE.UPLOAD_FILE') }}
          </button>
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSyncing || items.length === 0"
            :title="$t('LIA_AGENTS.KNOWLEDGE.SYNC_TOOLTIP')"
            @click="syncKnowledge"
          >
            <span
              v-if="isSyncing"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-refresh-cw size-4" />
            {{ $t('LIA_AGENTS.KNOWLEDGE.SYNC_BUTTON') }}
          </button>
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept=".pdf,.txt,.docx"
            @change="handleFileUpload"
          />
        </div>
      </div>

      <!-- Loading -->
      <div
        v-if="isLoading && items.length === 0"
        class="flex items-center justify-center py-12"
      >
        <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-11" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="items.length === 0"
        class="text-center py-12 bg-n-alpha-2 rounded-lg"
      >
        <div
          class="w-14 h-14 mx-auto mb-4 rounded-full bg-n-alpha-3 flex items-center justify-center"
        >
          <span class="i-lucide-book-open size-7 text-n-slate-11" />
        </div>
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          {{ $t('LIA_AGENTS.KNOWLEDGE.EMPTY_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11 max-w-sm mx-auto">
          {{ $t('LIA_AGENTS.KNOWLEDGE.EMPTY_DESCRIPTION') }}
        </p>
      </div>

      <!-- Items List -->
      <div v-else class="space-y-2">
        <div
          v-for="item in items"
          :key="item.id"
          class="flex items-center gap-3 p-3 bg-n-alpha-2 rounded-lg hover:bg-n-alpha-3 transition-colors"
        >
          <!-- Icon -->
          <div
            class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            :class="
              item.status === 'ready'
                ? 'bg-green-100 dark:bg-green-900/30'
                : item.status === 'error'
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : 'bg-n-alpha-3'
            "
          >
            <span
              :class="sourceTypeIcon(item.source_type)"
              class="size-4 text-n-slate-11"
            />
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ item.title }}
            </p>
            <div class="flex items-center gap-2 mt-0.5">
              <span class="text-xs text-n-slate-11">
                {{ sourceTypeLabel(item.source_type) }}
              </span>
              <span
                v-if="item.chunks_count > 0"
                class="text-xs text-n-slate-11"
              >
                &middot; {{ item.chunks_count }}
                {{ $t('LIA_AGENTS.KNOWLEDGE.CHUNKS') }}
              </span>
              <span
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium"
                :class="statusConfig[item.status]?.class"
              >
                <span :class="statusConfig[item.status]?.icon" class="size-3" />
                {{ statusConfig[item.status]?.label }}
              </span>
            </div>
            <p
              v-if="item.status === 'error' && item.error_message"
              class="text-xs text-red-600 dark:text-red-400 mt-1 truncate"
            >
              {{ item.error_message }}
            </p>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1 flex-shrink-0">
            <button
              v-if="item.source_type === 'text'"
              type="button"
              class="p-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-3 rounded-lg transition-colors"
              :title="$t('LIA_AGENTS.KNOWLEDGE.EDIT_BUTTON')"
              @click="openEditText(item)"
            >
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              v-if="
                item.status === 'pending' ||
                item.status === 'error' ||
                item.status === 'ready'
              "
              type="button"
              class="p-1.5 rounded-lg transition-colors"
              :class="
                item.status === 'pending'
                  ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 animate-pulse'
                  : 'text-n-slate-11 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              "
              :title="
                item.status === 'pending'
                  ? 'Processar agora'
                  : $t('LIA_AGENTS.KNOWLEDGE.REPROCESS_BUTTON')
              "
              @click="reprocessItem(item)"
            >
              <span
                v-if="item.status === 'pending'"
                class="i-lucide-play size-4"
              />
              <span v-else class="i-lucide-refresh-cw size-4" />
            </button>
            <button
              type="button"
              class="p-1.5 text-n-slate-11 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              :title="$t('LIA_AGENTS.KNOWLEDGE.DELETE_BUTTON')"
              @click="deleteItem(item)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Info -->
      <div class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p
          class="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2"
        >
          <span class="i-lucide-info size-4 flex-shrink-0 mt-0.5" />
          {{ $t('LIA_AGENTS.KNOWLEDGE.INFO') }}
        </p>
      </div>
    </div>

    <!-- Add/Edit Text Modal -->
    <div
      v-if="showAddTextModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="showAddTextModal = false"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-xl max-h-[80vh] flex flex-col"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-n-weak"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              editingItem
                ? $t('LIA_AGENTS.KNOWLEDGE.MODAL_EDIT_TITLE')
                : $t('LIA_AGENTS.KNOWLEDGE.MODAL_ADD_TITLE')
            }}
          </h3>
          <button
            type="button"
            class="p-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg"
            @click="showAddTextModal = false"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <div class="p-4 space-y-4 overflow-y-auto flex-1">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('LIA_AGENTS.KNOWLEDGE.FIELD_TITLE_LABEL') }}
            </label>
            <input
              v-model="formTitle"
              type="text"
              :placeholder="$t('LIA_AGENTS.KNOWLEDGE.TITLE_PLACEHOLDER')"
              class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500 text-sm"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('LIA_AGENTS.KNOWLEDGE.FIELD_CONTENT_LABEL') }}
            </label>
            <textarea
              v-model="formContent"
              rows="12"
              :placeholder="$t('LIA_AGENTS.KNOWLEDGE.CONTENT_PLACEHOLDER')"
              class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500 text-sm resize-none"
            />
          </div>
        </div>
        <div class="flex gap-2 p-4 border-t border-n-weak">
          <button
            type="button"
            class="flex-1 px-4 py-2 text-sm font-medium text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors"
            @click="showAddTextModal = false"
          >
            {{ $t('LIA_AGENTS.KNOWLEDGE.CANCEL') }}
          </button>
          <button
            type="button"
            class="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isSaving || !formTitle.trim() || !formContent.trim()"
            @click="saveTextItem"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{
              editingItem
                ? $t('LIA_AGENTS.KNOWLEDGE.UPDATE')
                : $t('LIA_AGENTS.KNOWLEDGE.ADD')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
